// src/services/apiClient.js

import { loadingController } from "./loadingController";
import { warmupController } from "./warmupController";

let logoutHandlers = {
  vendor: null,
  user: null,
};

export const setLogoutHandler = (type, handler) => {
  if (type === "vendor" || type === "user") {
    logoutHandlers[type] = handler;
  }
};

/**
 * API origin without trailing slash. Empty string = same-origin `/api/...`
 * (use with a host rewrite/proxy so iOS Safari keeps first-party HttpOnly cookies).
 */
export function getApiOrigin() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  return s.replace(/\/+$/, "");
}

/** Absolute or same-origin path for raw `fetch` (e.g. multipart). */
export function buildApiUrl(path) {
  const base = getApiOrigin();
  const p = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${p}` : p;
}

const USER_JWT_KEY = "user_bearer_token";
const VENDOR_JWT_KEY = "vendor_bearer_token";

const PUBLIC_USER_NO_BEARER = new Set([
  "/api/users/login",
  "/api/users/google-login",
  "/api/users/signup-send-otp",
  "/api/users/verify-signup-otp",
]);

function isPublicVendorPath(p) {
  return (
    p === "/api/vendors/storefront" ||
    p === "/api/vendors/login" ||
    p === "/api/vendors/google-login" ||
    p === "/api/vendors/register" ||
    p === "/api/vendors/products" ||
    p.startsWith("/api/vendors/products/") ||
    p.startsWith("/api/vendors/store/")
  );
}

/**
 * HttpOnly cookies when same-site; iOS Safari often blocks cross-site cookies — then
 * session JWT from login JSON is kept in localStorage and sent as Authorization Bearer.
 */
const buildAuthHeaders = (_method, pathname, baseHeaders) => {
  const p = String(pathname || "").split("?")[0];
  const h = { ...baseHeaders };
  if (typeof localStorage === "undefined") return h;

  // Vendor JWT: storefront routes + /api/products (create/update/delete use verifyToken on the API).
  const vendorNeedsBearer =
    (p.startsWith("/api/vendors") && !isPublicVendorPath(p)) ||
    p.startsWith("/api/vendor-products") ||
    p.startsWith("/api/coupons") ||
    p.startsWith("/api/products");

  if (vendorNeedsBearer) {
    const t = localStorage.getItem(VENDOR_JWT_KEY);
    if (t) h.Authorization = `Bearer ${t}`;
    return h;
  }

  if (
    (p.startsWith("/api/users") ||
      p.startsWith("/api/wishlist") ||
      p.startsWith("/api/orders") ||
      p.startsWith("/api/reviews")) &&
    !PUBLIC_USER_NO_BEARER.has(p)
  ) {
    const t = localStorage.getItem(USER_JWT_KEY);
    if (t) h.Authorization = `Bearer ${t}`;
  }

  return h;
};

export function setUserSessionToken(token) {
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(USER_JWT_KEY, token);
  else localStorage.removeItem(USER_JWT_KEY);
}

export function setVendorSessionToken(token) {
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(VENDOR_JWT_KEY, token);
  else localStorage.removeItem(VENDOR_JWT_KEY);
}

const isColdStartLikely = (status, message) => {
  // Render cold start frequently manifests as gateway/timeouts or generic network failures.
  if ([502, 503, 504].includes(status)) return true;
  const m = (message || "").toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network error") ||
    m.includes("timeout") ||
    m.includes("load failed")
  );
};

const handleResponse = async (response, url, method) => {
  const contentType = response.headers.get("content-type");
  const data = contentType?.includes("application/json")
    ? await response.json()
    : await response.text();

  if (response.status === 401 || response.status === 403) {
    const path = url.split("?")[0];
    const m = (method || "GET").toUpperCase();

    const authFailurePublic =
      path === "/api/users/login" ||
      path === "/api/users/google-login" ||
      path === "/api/vendors/login" ||
      path === "/api/vendors/google-login";

    /** 401 here means "no session yet", not expired session — never redirect/reload. */
    const isSessionBootstrapProbe =
      m === "GET" &&
      (path === "/api/vendors/profile" || path === "/api/users/profile");

    if (!authFailurePublic && !isSessionBootstrapProbe) {
      const vendorUnauthorized =
        path.startsWith("/api/vendors") ||
        path.startsWith("/api/vendor-products") ||
        path.startsWith("/api/coupons") ||
        path.startsWith("/api/products/") ||
        (path === "/api/products" && m !== "GET");
      const userUnauthorized =
        path.startsWith("/api/users") ||
        path.startsWith("/api/wishlist") ||
        path.startsWith("/api/reviews") ||
        path.startsWith("/api/orders");

      if (vendorUnauthorized && logoutHandlers.vendor) {
        await logoutHandlers.vendor();
      } else if (userUnauthorized && logoutHandlers.user) {
        await logoutHandlers.user();
      }
    }

    const msg =
      authFailurePublic || isSessionBootstrapProbe
        ? data?.message || data?.error || response.statusText
        : "Session expired";
    throw new Error(msg);
  }

  if (!response.ok) {
    const error = data?.message || data?.error || response.statusText;
    throw new Error(error);
  }

  // When the SPA is built with same-origin `/api` but the host does not proxy to the
  // backend, `/api/*` often returns this site's index.html — treat as error instead of
  // returning a string (which breaks callers expecting JSON and can blank the UI).
  const path = String(url || "").split("?")[0];
  if (
    path.startsWith("/api") &&
    typeof data === "string" &&
    /^\s*</.test(data)
  ) {
    throw new Error(
      "API returned HTML instead of JSON. If REACT_APP_API_URL is empty, your host must rewrite /api/* to your backend, or set REACT_APP_API_URL to the full API base URL.",
    );
  }

  return data;
};

export const apiClient = async (method, url, body = null, options = {}) => {
  const {
    isJson = true,
    headers = {},
    skipLoading = false,
    params = {}, // new field for query params
    retryOnWarmup = true,
  } = options;

  // Convert params object to query string
  const queryString = Object.keys(params).length
    ? "?" + new URLSearchParams(params).toString()
    : "";

  const methodUpper = method.toUpperCase();
  const baseHeaders =
    isJson && body
      ? { "Content-Type": "application/json", ...headers }
      : { ...headers };

  const config = {
    method: methodUpper,
    credentials: "include",
    headers: buildAuthHeaders(methodUpper, url, baseHeaders),
  };

  if (body) {
    config.body = isJson ? JSON.stringify(body) : body;
  }

  if (!skipLoading) loadingController.show();

  try {
    const res = await fetch(`${buildApiUrl(url)}${queryString}`, config);
    if (retryOnWarmup && isColdStartLikely(res.status)) {
      if (res.headers.get("x-platform-maintenance") === "1") {
        if (!skipLoading) loadingController.hide();
        return await handleResponse(res, url, methodUpper);
      }
      // Show popup once, wait, then retry the same request one time.
      if (!skipLoading) loadingController.hide();
      await warmupController.show({ seconds: 60 });
      return await apiClient(method, url, body, {
        ...options,
        retryOnWarmup: false,
      });
    }
    if (!skipLoading) loadingController.hide();
    return await handleResponse(res, url, methodUpper);
  } catch (error) {
    if (!skipLoading) loadingController.hide();
    if (retryOnWarmup && isColdStartLikely(undefined, error?.message)) {
      await warmupController.show({ seconds: 60 });
      return await apiClient(method, url, body, {
        ...options,
        retryOnWarmup: false,
      });
    }
    throw new Error(`API call failed: ${error.message}`);
  }
};

export const persistUserBearerToken = () => {
  setUserSessionToken(null);
};

export const persistVendorBearerToken = () => {
  setVendorSessionToken(null);
};
